import {gql} from '@apollo/client';
import {useCallback, useRef, useState} from 'react';
import {showToast} from '../../../common';
import {captureMessage} from 'monitoring';
import {getDomainGraphqlError, useMutation$} from '../../../graphqlsupport';
import {ContactFormSubmission} from './ContactForm';
import {BusinessContactRow} from './domain/types';

/**
 * confirmDuplicate is a Java primitive on the backend input, so it renders non-null and every call
 * has to carry it. A first attempt always says false: the backend answers with a duplicate code and
 * the user decides.
 */
type ContactMutationInput = ContactFormSubmission & {confirmDuplicate: boolean};

export const CREATE_BUSINESS_CONTACT = gql`
    mutation createBusinessContact($input: BusinessContactCreateInput!) {
        createBusinessContact(input: $input) {
            guid
        }
    }
`;

export const UPDATE_BUSINESS_CONTACT = gql`
    mutation updateBusinessContact($input: BusinessContactUpdateInput!) {
        updateBusinessContact(input: $input) {
            guid
        }
    }
`;

/** Shown when the backend fails without a message of its own, so the user never sees a blank error. */
const FALLBACK_ERROR = 'We could not save this contact. Please try again.';

/**
 * Sharing only an email, or only a phone, with an existing contact. Two people at one practice do
 * share a switchboard number, so the backend asks rather than refuses: resend the same payload with
 * confirmDuplicate set and it goes through.
 *
 * <p>The two hard stops, a shared name plus email and a shared name plus phone, are deliberately not
 * here. Nothing the user clicks clears those, so they show as a plain error.
 */
const CONFIRMABLE_DUPLICATE_CODES = [
    'DUPLICATE_BUSINESS_CONTACT_EMAIL',
    'DUPLICATE_BUSINESS_CONTACT_PHONE',
];

/** PortalGraphQLError#getExtensions is what puts the code on the wire beside the message. */
const domainErrorCode = (e: any): string => e?.graphQLErrors?.[0]?.extensions?.errorCode ?? '';

/** A save the backend paused on a possible duplicate, held so the user's answer can resend it. */
type PendingDuplicate = {
    contact: BusinessContactRow | null;
    values: ContactFormSubmission;
    message: string;
};

type SavedContact = {guid: string};

/**
 * The Contact Management save path (BHPNPP-3241). A contact with a guid goes to update, one without
 * goes to create, and the caller does not have to know which.
 *
 * <p>A failure keeps the drawer open with the backend's own message on screen. Auto-dismissing it
 * would leave the user looking at a form they think saved.
 */
export const useBusinessContactMutation = (onSaved: () => void) => {
    const create = useMutation$<{input: ContactMutationInput}, {createBusinessContact: SavedContact}>(
        CREATE_BUSINESS_CONTACT);
    const update = useMutation$<{input: ContactMutationInput & {guid: string}}, {updateBusinessContact: SavedContact}>(
        UPDATE_BUSINESS_CONTACT);

    const [saving, setSaving] = useState(false);
    // The state drives the buttons, but a button only goes grey: FatOutlineButton renders
    // pointer-events-none rather than the disabled attribute, so Enter on the still-focused Save
    // button gets through. This ref is what actually stops a second save, and it is a ref because two
    // submits in one tick would both read the same stale state.
    const savingRef = useRef(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [pendingDuplicate, setPendingDuplicate] = useState<PendingDuplicate | null>(null);

    const send = useCallback(async (
        contact: BusinessContactRow | null, values: ContactFormSubmission, confirmDuplicate: boolean) => {
        if (savingRef.current) {
            return;
        }
        savingRef.current = true;
        setSaving(true);
        setErrorMessage('');
        // What the save did, decided inside the try and acted on outside it. Everything after the
        // mutation returns - the toast, closing the drawer, refetching the grid - is a render pass
        // that can throw, and a throw from there inside the try would be reported to the user as a
        // failed save on a contact that was in fact written. They would then save it again.
        let savedMessage = '';
        try {
            if (contact) {
                await update.execute({variables: {input: {...values, guid: contact.guid, confirmDuplicate}}});
                savedMessage = 'Contact updated.';
            } else {
                await create.execute({variables: {input: {...values, confirmDuplicate}}});
                savedMessage = 'Contact added.';
            }
        } catch (e) {
            const message = getDomainGraphqlError(e) || FALLBACK_ERROR;
            if (CONFIRMABLE_DUPLICATE_CODES.includes(domainErrorCode(e))) {
                setPendingDuplicate({contact, values, message});
            } else {
                // Drop the question as well as raising the error. A resend that fails for some other
                // reason would otherwise leave the duplicate dialog sitting on top of the message the
                // user needs to read.
                setPendingDuplicate(null);
                setErrorMessage(message);
            }
        } finally {
            savingRef.current = false;
            setSaving(false);
        }

        if (savedMessage !== '') {
            showToast.success(savedMessage);
            setPendingDuplicate(null);
            try {
                onSaved();
            } catch (e) {
                // The contact is already written. Closing the drawer and refreshing the grid can still
                // fail, and putting that on screen over a green toast invites the user to save the
                // same contact a second time. It goes to Sentry instead.
                captureMessage(`Business contact saved but the drawer refresh failed: ${(e as Error).message}`);
            }
        }
    }, [create, update, onSaved]);

    const save = useCallback((contact: BusinessContactRow | null, values: ContactFormSubmission) =>
        send(contact, values, false), [send]);

    /** The user said the duplicate is fine. The same payload goes back with the flag set. */
    const confirmDuplicate = useCallback(() => {
        if (pendingDuplicate) {
            return send(pendingDuplicate.contact, pendingDuplicate.values, true);
        }
    }, [pendingDuplicate, send]);

    /**
     * The user backed out. Nothing is sent, and the drawer stays open on their typed values.
     *
     * <p>Refuses while a save is running. The dialog stays up for the whole of a confirmed resend, and
     * backing out mid-resend cannot un-send it: clearing the question there would close the dialog on
     * a contact that is about to be written anyway.
     */
    const dismissDuplicate = useCallback(() => {
        if (!savingRef.current) {
            setPendingDuplicate(null);
        }
    }, []);

    /** Clears a stale message when the drawer moves to another contact. */
    const clearError = useCallback(() => {
        setErrorMessage('');
        setPendingDuplicate(null);
    }, []);

    return {
        save,
        saving,
        errorMessage,
        clearError,
        duplicateMessage: pendingDuplicate?.message ?? '',
        /** A question is on screen and unanswered. The drawer must not move while it is. */
        duplicatePending: pendingDuplicate !== null,
        confirmDuplicate,
        dismissDuplicate,
    };
};
